<template>
  <div class="performance-bar-chart">
    <!-- Filtros -->
    <div class="filters">
      <label>Tipo de Licitação:</label>
      <select
        v-model="filters.biddingTypeSelecionado"
        class="filter-select"
        @change="updateChart"
      >
        <option value="">
          Todos
        </option>
        <option
          v-for="type in biddingTypes"
          :key="type"
          :value="type"
        >
          {{ type }}
        </option>
      </select>

      <label class="ml-3">Período:</label>
      <select
        v-model="filters.period"
        class="filter-select"

        @change="updateChart"
      >
        <option value="daily">
          Diário
        </option>
        <option value="weekly">
          Semanal
        </option>
        <option value="monthly">
          Mensal
        </option>
        <option value="quarterly">
          Trimestral
        </option>
      </select>

      <!-- Filtro Mês aparece para período semanal e diário -->
      <label
        v-if="filters.period === 'weekly' || filters.period === 'daily'"
        class="ml-3"
      >Mês:</label>
      <select
        v-if="filters.period === 'weekly' || filters.period === 'daily'"
        v-model="filters.month"
        class="filter-select"
        @change="updateChart"
      >
        <option value="">
          Todos
        </option>
        <option
          v-for="m in months"
          :key="m.value"
          :value="m.value"
        >
          {{ m.name }}
        </option>
      </select>

      <label class="ml-3">Ano:</label>
      <select
        v-model="filters.year"
        class="filter-select"
        @change="updateChart"
      >
        <option
          v-for="y in availableYears"
          :key="y"
          :value="y"
        >
          {{ y }}
        </option>
      </select>
    </div>

    <!-- Gráfico -->
    <bar-chart
      :chart-data="chartData"
      :options="chartOptions"
    />
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs'
import { reactiveProp } from 'vue-chartjs/es/mixins'
import fetchReportData from '@/syslic/dashboard/reportService'
import filterBiddingSuccessData from '@/syslic/dashboard/perfomance/filterBiddingSuccessData'

export default {
  name: 'PerformanceBarChart',
  components: {
    barChart: {
      extends: Bar,
      mixins: [reactiveProp],
      props: ['chartData', 'options'],
      mounted() {
        this.renderChart(this.chartData, this.options)
      },
      watch: {
        chartData(newData) {
          this.renderChart(newData, this.options)
        },
      },
    },
  },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      rawData: [],
      chartData: { labels: [], datasets: [] },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              id: 'y-quantidade',
              position: 'left',
              ticks: { beginAtZero: true },
              scaleLabel: { display: true, labelString: 'Quantidade' },
            },
            {
              id: 'y-conversao',
              position: 'right',
              ticks: { beginAtZero: true, max: 100 },
              scaleLabel: { display: true, labelString: 'Taxa de Conversão (%)' },
              gridLines: { drawOnChartArea: false },
            },
          ],
          xAxes: [
            {
              scaleLabel: { display: true, labelString: 'Período' },
            },
          ],
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex]
              const val = dataset.data[tooltipItem.index]
              return `${dataset.label}: ${typeof val === 'number' ? val.toFixed(2) : val}`
            },
          },
        },
        legend: { position: 'top' },
      },
      filters: {
        biddingTypeSelecionado: '',
        period: 'monthly',
        year: currentYear,
        month: '', // filtro mês, só usado para período semanal e diário
      },
      biddingTypes: [
        'Aquisição',
        'Aviso de Contratação Direta',
        'Edital',
        'SRP',
      ],
      availableYears: Array.from({ length: 5 }, (_, i) => currentYear - i),
      months: [
        { value: '1', name: 'Janeiro' },
        { value: '2', name: 'Fevereiro' },
        { value: '3', name: 'Março' },
        { value: '4', name: 'Abril' },
        { value: '5', name: 'Maio' },
        { value: '6', name: 'Junho' },
        { value: '7', name: 'Julho' },
        { value: '8', name: 'Agosto' },
        { value: '9', name: 'Setembro' },
        { value: '10', name: 'Outubro' },
        { value: '11', name: 'Novembro' },
        { value: '12', name: 'Dezembro' },
      ],
    }
  },
  watch: {
    'filters.biddingTypeSelecionado': 'updateChart',
    'filters.period': 'updateChart',
    'filters.year': 'updateChart',
    'filters.month': 'updateChart',
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.rawData = await fetchReportData(this)
      this.updateChart()
    },
    updateChart() {
      const processed = filterBiddingSuccessData(this.rawData, this.filters)

      this.chartData = {
        labels: processed.labels,
        datasets: [
          {
            label: 'Licitações Ganhas',
            backgroundColor: '#4CAF50',
            data: processed.datasets[0]?.data || [],
            yAxisID: 'y-quantidade',
          },
          {
            label: 'Itens Ganhos',
            backgroundColor: '#2196F3',
            data: processed.datasets[1]?.data || [],
            yAxisID: 'y-quantidade',
          },
          {
            label: 'Taxa de Conversão (%)',
            backgroundColor: '#FFC107',
            data: processed.datasets[2]?.data || [],
            yAxisID: 'y-conversao',
          },
        ],
      }
    },
  },
}
</script>

<style scoped>
.filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
}
.filter-select {
  padding: 0.5rem;
  font-size: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  max-width: 150px;
  width: 100%;
}
.ml-3 {
  margin-left: 1rem;
}

.chart-box {
  width: 100%;
  height: 400px;
}
</style>
